import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  model,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AdvancedSearchService } from '@lassi/webapp-shared';

@Component({
  selector: 'lassi-address-search-form',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
  ],
  template: `
    <div class="row">
      <mat-form-field class="long-hint-form-field">
        <mat-label>Unit Number</mat-label>
        <input
          matInput
          name="unitNumber"
          maxlength="15"
          [(ngModel)]="unitNumber"
          #unitNumberInput="ngModel"
          [pattern]="starsWithNumberPattern"
        />
        <mat-hint class="mat-body-small">Eg. 5a or 5.6</mat-hint>
        @if (unitNumberInput.hasError('pattern')) {
          <mat-error>Must start with a number</mat-error>
        }
      </mat-form-field>

      <mat-form-field class="long-hint-form-field">
        <mat-label>Number</mat-label>
        <input
          matInput
          name="number"
          maxlength="15"
          [(ngModel)]="number"
          #numberInput="ngModel"
          [pattern]="starsWithNumberPattern"
        />
        <mat-hint class="mat-body-small">Eg. 5a or 21-23</mat-hint>
        @if (numberInput.hasError('pattern')) {
          <mat-error>Must start with a number</mat-error>
        }
      </mat-form-field>
    </div>

    <mat-form-field class="long-hint-form-field">
      <mat-label>Street name</mat-label>
      <input
        matInput
        name="streetName"
        maxlength="45"
        [(ngModel)]="streetName"
      />
      <mat-hint class="mat-body-small"
        >Eg. Sutherland or 37th Parallel
      </mat-hint>
    </mat-form-field>

    <mat-form-field class="long-hint-form-field">
      <mat-label>Street type</mat-label>
      <input
        type="text"
        matInput
        name="streetType"
        [(ngModel)]="streetType"
        [matAutocomplete]="auto"
        maxlength="15"
        #streetTypeInput="ngModel"
        (input)="handleStreetTypeChange($event)"
        (focus)="handleStreetTypeChange($event)"
      />
      <mat-autocomplete
        #auto="matAutocomplete"
        [requireSelection]="true"
        [displayWith]="roadTypeDisplay"
      >
        @if (roadOptionsError()) {
          <mat-option [disabled]="true">Unable to load street types</mat-option>
        } @else if (roadOptionsLoaded()) {
          @for (type of filteredRoadTypes(); track type) {
            <mat-option [value]="type.value">{{ type.name }}</mat-option>
          }
        } @else {
          <mat-option [disabled]="true">Loading...</mat-option>
        }
      </mat-autocomplete>
      <mat-hint class="mat-body-small"
        >Leave blank to search with all matching types
      </mat-hint>
      @if (roadOptionsError()) {
        <mat-error
          >Unable to load street types. Please try again later.
        </mat-error>
      }
    </mat-form-field>

    <mat-form-field>
      <mat-label>Street Suffix</mat-label>
      <mat-select name="streetSuffix" [(ngModel)]="streetSuffix">
        @if (roadOptionsError()) {
          <mat-option [disabled]="true"
            >Unable to load street suffixes</mat-option
          >
        } @else if (roadOptionsLoaded()) {
          <mat-option [value]="undefined">-</mat-option>
          @for (suffix of roadSuffixes(); track suffix) {
            <mat-option [value]="suffix.value">
              {{ suffix.name }}
            </mat-option>
          }
        } @else {
          <mat-option [disabled]="true">Loading...</mat-option>
        }
      </mat-select>
      @if (roadOptionsError()) {
        <mat-error
          >Unable to load street suffixes. Please try again later.
        </mat-error>
      }
    </mat-form-field>

    <mat-form-field>
      <mat-label>Suburb/Locality/Town</mat-label>
      <input matInput name="locality" [(ngModel)]="locality" maxlength="46" />
      <mat-hint class="mat-body-small"
        >Leave blank to search with any locality names
      </mat-hint>
    </mat-form-field>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-top: 0.5rem;
    }

    mat-form-field {
      padding-bottom: 0.5rem;
    }

    .long-hint-form-field {
      padding-bottom: 1.25rem;
    }

    .row {
      display: flex;
      gap: 1rem;
      width: 100%;
      padding-top: 0.2rem;
    }
  `,
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressSearchFormComponent {
  unitNumber = model<string | null>();
  number = model<string | null>();
  streetName = model<string | null>();
  streetType = model<string | null>();
  streetSuffix = model<string | null>();
  locality = model<string | null>();

  private readonly advancedSearchService = inject(AdvancedSearchService);

  readonly starsWithNumberPattern = /^\d/;

  roadOptionsResource = rxResource({
    stream: () => this.advancedSearchService.searchAddressForRoadOptions(),
  });

  roadTypeDisplay(value: string) {
    if (!value) {
      return '';
    }
    return value[0].toUpperCase() + value.slice(1).toLowerCase();
  }

  filteredRoadTypes = computed(() => {
    const filterValue = this.streetType()?.toLowerCase() ?? '';
    const types = this.roadOptionsResource.value()?.roadTypes;
    return (
      types?.filter((type) => type.value.toLowerCase().includes(filterValue)) ||
      []
    );
  });
  roadSuffixes = computed(() => this.roadOptionsResource.value()?.roadSuffixes);
  roadOptionsLoaded = computed(
    () =>
      !this.roadOptionsResource.isLoading() &&
      !this.roadOptionsResource.error(),
  );
  roadOptionsError = this.roadOptionsResource.error;

  handleStreetTypeChange(event: Event) {
    const el = event.target as HTMLInputElement;
    this.streetType.set(el.value);
  }
}
